import { richTextToPlain, sanitizeRichText } from './rich-text.js';

/// O contrato do Prontuário (ADR 0038) que servidor e tela precisam ler igual: a forma do modelo
/// de ficha, a forma da resposta, o que uma resposta diz ao Quadro de saúde e o que mudou entre
/// duas versões. Escrito duas vezes, a tela e o servidor discordariam justamente sobre o que é
/// dado de saúde.

/// Os oito tipos de resposta formam lista fechada: impressão, leitura, comparação e Quadro de saúde
/// precisam saber tratar cada um, e tipo novo é mudança de sistema (ADR 0038, decisão 3).
export const RECORD_QUESTION_TYPES = [
  'SHORT_TEXT',
  'LONG_TEXT',
  'YES_NO',
  'SINGLE_CHOICE',
  'MULTIPLE_CHOICE',
  'DECLARATION',
  'NUMBER',
  'DATE',
] as const;
export type RecordQuestionType = (typeof RECORD_QUESTION_TYPES)[number];

export const RECORD_BLOCK_KINDS = ['HEADING', 'SUBHEADING', 'DESCRIPTION', 'QUESTION'] as const;
export type RecordBlockKind = (typeof RECORD_BLOCK_KINDS)[number];

/// Os tipos cuja resposta pode falar de saúde. Texto livre não entra no Quadro: o sistema não tem
/// como saber se "nenhuma" escrito à mão é uma negação.
export const THEMED_QUESTION_TYPES: readonly RecordQuestionType[] = [
  'YES_NO',
  'SINGLE_CHOICE',
  'MULTIPLE_CHOICE',
];

export interface RecordOption {
  id: string;
  label: string;
}

export interface RecordQuestion {
  id: string;
  kind: 'QUESTION';
  label: string;
  help: string | null;
  type: RecordQuestionType;
  required: boolean;
  /// Só nas duas escolhas, na ordem em que aparecem.
  options: RecordOption[];
  /// Só nas caixas de marcação: a opção que desmarca as outras e nunca entra no Quadro.
  exclusiveOptionId: string | null;
  /// Só no sim ou não: o que perguntar quando a resposta é Sim.
  detailLabel: string | null;
  /// Tema do sistema (`ALLERGY`) ou o ID de um tema da empresa.
  themeKey: string | null;
  /// Só na escolha única com tema: as opções que entram no Quadro.
  flaggedOptionIds: string[];
}

export type RecordBlock =
  | { id: string; kind: 'HEADING'; text: string }
  | { id: string; kind: 'SUBHEADING'; text: string }
  | { id: string; kind: 'DESCRIPTION'; html: string }
  | RecordQuestion;

export interface YesNoAnswer {
  value: boolean;
  detail: string | null;
}

/// Texto curto, texto formatado, número (`12.5`), data (`2026-09-25`) e escolha única (o ID da
/// opção) são texto; caixas de marcação são a lista de IDs; declaração é `true`.
export type RecordAnswer = string | string[] | boolean | YesNoAnswer;
export type RecordAnswers = Record<string, RecordAnswer>;

export interface RecordIssue {
  /// O ID do bloco, ou `null` quando o problema é do modelo inteiro.
  blockId: string | null;
  message: string;
}

// ---------------------------------------------------------------------------------------------
// Temas de saúde
// ---------------------------------------------------------------------------------------------

/// As cores possíveis da etiqueta, pelos tons de significado do design system.
export const HEALTH_THEME_COLORS = [
  'danger',
  'warning',
  'progress',
  'brand',
  'positive',
  'neutral',
] as const;
export type HealthThemeColor = (typeof HEALTH_THEME_COLORS)[number];

export interface HealthTheme {
  key: string;
  displayName: string;
  color: HealthThemeColor;
  /// Um Não numa ficha contradiz um Sim em outra.
  flagsDivergence: boolean;
  system: boolean;
  active: boolean;
}

/// Um tema é um fato só: gestação e amamentação são dois, porque um Não numa não contradiz o Sim na
/// outra.
///
/// A base do sistema: constantes, não linhas no banco, porque ativar o módulo não semeia nada (R1)
/// e porque o sistema age sobre elas — nome e regra são travados (ADR 0038, decisão 7).
export const SYSTEM_HEALTH_THEMES: readonly HealthTheme[] = Object.freeze(
  (
    [
      ['ALLERGY', 'Alergia', 'danger', true],
      ['MEDICATION', 'Medicamento', 'progress', true],
      ['HEALTH_CONDITION', 'Condição de saúde', 'progress', false],
      ['PREGNANCY', 'Gestação', 'warning', true],
      ['BREASTFEEDING', 'Amamentação', 'warning', true],
      ['SENSITIVITY', 'Sensibilidade', 'warning', false],
      ['RECENT_CHEMISTRY', 'Química recente', 'neutral', false],
    ] as const
  ).map(([key, displayName, color, flagsDivergence]) =>
    Object.freeze({ key, displayName, color, flagsDivergence, system: true, active: true }),
  ),
);

export function isSystemHealthTheme(key: string): boolean {
  return SYSTEM_HEALTH_THEMES.some((theme) => theme.key === key);
}

// ---------------------------------------------------------------------------------------------
// Modelo de ficha
// ---------------------------------------------------------------------------------------------

const MAX_BLOCKS = 200;
const MAX_OPTIONS = 40;

/// Confere e limpa os blocos de um modelo. Devolve os blocos sem os campos que o tipo não usa — a
/// escolha única que virou texto curto não carrega as opções antigas para a versão publicada.
///
/// `themeKeys` são os temas que a pergunta pode citar: os do sistema e os da empresa, inclusive
/// os desativados que ela já usava.
export function reviewTemplateBlocks(
  blocks: readonly RecordBlock[],
  themeKeys: ReadonlySet<string>,
): { blocks: RecordBlock[]; issues: RecordIssue[] } {
  const issues: RecordIssue[] = [];
  if (blocks.length > MAX_BLOCKS) {
    issues.push({ blockId: null, message: `Use até ${MAX_BLOCKS} blocos no modelo.` });
  }
  const seen = new Set<string>();
  const reviewed = blocks.map((block) => {
    if (seen.has(block.id)) issues.push({ blockId: block.id, message: 'Bloco repetido.' });
    seen.add(block.id);
    const { block: clean, message } = reviewBlock(block, themeKeys);
    if (message) issues.push({ blockId: block.id, message });
    return clean;
  });
  return { blocks: reviewed, issues };
}

/// O que o editor de um bloco mostra ao lado do botão de aplicar. Nulo quando está certo.
export function blockIssue(block: RecordBlock, themeKeys: ReadonlySet<string>): string | null {
  return reviewBlock(block, themeKeys).message;
}

function reviewBlock(
  block: RecordBlock,
  themeKeys: ReadonlySet<string>,
): { block: RecordBlock; message: string | null } {
  if (block.kind === 'HEADING' || block.kind === 'SUBHEADING') {
    const text = collapse(block.text);
    const message = !text
      ? `Escreva o ${block.kind === 'HEADING' ? 'título' : 'subtítulo'}.`
      : text.length > 160
        ? 'Use até 160 caracteres.'
        : null;
    return { block: { id: block.id, kind: block.kind, text }, message };
  }
  if (block.kind === 'DESCRIPTION') {
    const html = sanitizeRichText(block.html);
    const message = !richTextToPlain(html)
      ? 'Escreva a descrição.'
      : html.length > 10_000
        ? 'A descrição passou do tamanho máximo.'
        : null;
    return { block: { id: block.id, kind: 'DESCRIPTION', html }, message };
  }
  return reviewQuestion(block, themeKeys);
}

function reviewQuestion(
  question: RecordQuestion,
  themeKeys: ReadonlySet<string>,
): { block: RecordQuestion; message: string | null } {
  const choice = question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE';
  const themed = THEMED_QUESTION_TYPES.includes(question.type);
  const options = choice
    ? question.options.map((option) => ({ id: option.id, label: collapse(option.label) }))
    : [];
  const optionIds = new Set(options.map((option) => option.id));
  const themeKey = themed ? question.themeKey : null;
  const exclusiveOptionId =
    question.type === 'MULTIPLE_CHOICE' && question.exclusiveOptionId
      ? question.exclusiveOptionId
      : null;
  const detail = question.type === 'YES_NO' ? collapse(question.detailLabel ?? '') : '';
  const block: RecordQuestion = {
    id: question.id,
    kind: 'QUESTION',
    label: collapse(question.label),
    help: collapse(question.help ?? '') || null,
    type: question.type,
    required: question.required,
    options,
    exclusiveOptionId,
    detailLabel: detail || null,
    themeKey,
    flaggedOptionIds:
      question.type === 'SINGLE_CHOICE' && themeKey ? [...new Set(question.flaggedOptionIds)] : [],
  };
  return { block, message: questionMessage(block, optionIds, themeKeys) };
}

function questionMessage(
  question: RecordQuestion,
  optionIds: ReadonlySet<string>,
  themeKeys: ReadonlySet<string>,
): string | null {
  const labelMax = question.type === 'DECLARATION' ? 600 : 300;
  if (question.label.length < 2) {
    return question.type === 'DECLARATION'
      ? 'Escreva o texto da declaração.'
      : 'Escreva a pergunta.';
  }
  if (question.label.length > labelMax) return `Use até ${labelMax} caracteres na pergunta.`;
  if ((question.help?.length ?? 0) > 300) return 'Use até 300 caracteres no texto explicativo.';
  if ((question.detailLabel?.length ?? 0) > 120) return 'Use até 120 caracteres no detalhe.';
  const choice = question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE';
  if (choice) {
    if (question.options.length < 2) return 'Informe pelo menos duas opções.';
    if (question.options.length > MAX_OPTIONS) return `Use até ${MAX_OPTIONS} opções.`;
    if (question.options.some((option) => !option.label)) return 'Escreva todas as opções.';
    if (question.options.some((option) => option.label.length > 120)) {
      return 'Use até 120 caracteres em cada opção.';
    }
    const labels = new Set(question.options.map((option) => fold(option.label)));
    if (labels.size !== question.options.length || optionIds.size !== question.options.length) {
      return 'Há opções repetidas.';
    }
  }
  if (question.exclusiveOptionId && !optionIds.has(question.exclusiveOptionId)) {
    return 'A opção que anula as outras não está na lista.';
  }
  if (question.themeKey && !themeKeys.has(question.themeKey)) {
    return 'Escolha um tema de saúde que exista.';
  }
  if (question.type === 'SINGLE_CHOICE' && question.themeKey) {
    if (!question.flaggedOptionIds.length) {
      return 'Escolha quais opções entram no Quadro de saúde.';
    }
    if (question.flaggedOptionIds.some((id) => !optionIds.has(id))) {
      return 'Uma opção marcada para o Quadro de saúde não está na lista.';
    }
  }
  return null;
}

export function questionsOf(blocks: readonly RecordBlock[]): RecordQuestion[] {
  return blocks.filter((block): block is RecordQuestion => block.kind === 'QUESTION');
}

// ---------------------------------------------------------------------------------------------
// Respostas
// ---------------------------------------------------------------------------------------------

const TEXT_MAX = 300;
const RICH_MAX = 20_000;

/// Confere as respostas contra as perguntas da versão do modelo que elas respondem. Resposta vazia
/// não é gravada; resposta de pergunta que o modelo não tem é recusada, porque não haveria como
/// mostrá-la depois.
export function reviewAnswers(
  blocks: readonly RecordBlock[],
  raw: Readonly<Record<string, unknown>>,
): { answers: RecordAnswers; issues: RecordIssue[] } {
  const questions = questionsOf(blocks);
  const known = new Set(questions.map((question) => question.id));
  const issues: RecordIssue[] = Object.keys(raw)
    .filter((id) => !known.has(id))
    .map((id) => ({ blockId: id, message: 'Essa pergunta não faz parte do modelo.' }));
  const answers: RecordAnswers = {};
  for (const question of questions) {
    const read = readAnswer(question, raw[question.id]);
    if (isIssue(read)) {
      issues.push({ blockId: question.id, message: read.issue });
    } else if (read !== undefined) {
      answers[question.id] = read;
    } else if (question.required) {
      issues.push({
        blockId: question.id,
        message:
          question.type === 'DECLARATION'
            ? 'Marque a declaração para salvar.'
            : 'Responda para salvar.',
      });
    }
  }
  return { answers, issues };
}

type Read = RecordAnswer | undefined | { issue: string };

function isIssue(read: Read): read is { issue: string } {
  return typeof read === 'object' && 'issue' in read;
}

function readAnswer(question: RecordQuestion, value: unknown): Read {
  if (value === undefined || value === null || value === '') return undefined;
  switch (question.type) {
    case 'SHORT_TEXT':
      return readText(value);
    case 'LONG_TEXT':
      return readRich(value);
    case 'YES_NO':
      return readYesNo(question, value);
    case 'SINGLE_CHOICE':
      return typeof value === 'string' && question.options.some((option) => option.id === value)
        ? value
        : { issue: 'Escolha uma das opções.' };
    case 'MULTIPLE_CHOICE':
      return readMultiple(question, value);
    case 'DECLARATION':
      return value === true ? true : value === false ? undefined : invalid();
    case 'NUMBER':
      return readNumber(value);
    case 'DATE':
      return readDate(value);
  }
}

function invalid(): { issue: string } {
  return { issue: 'Resposta inválida.' };
}

function readText(value: unknown): Read {
  if (typeof value !== 'string') return invalid();
  const text = collapse(value);
  if (text.length > TEXT_MAX) return { issue: `Use até ${TEXT_MAX} caracteres.` };
  return text || undefined;
}

function readRich(value: unknown): Read {
  if (typeof value !== 'string') return invalid();
  const html = sanitizeRichText(value);
  if (!richTextToPlain(html)) return undefined;
  return html.length > RICH_MAX ? { issue: 'O texto passou do tamanho máximo.' } : html;
}

function readYesNo(question: RecordQuestion, value: unknown): Read {
  if (typeof value !== 'object' || Array.isArray(value)) return invalid();
  const { value: answer, detail } = value as { value?: unknown; detail?: unknown };
  if (typeof answer !== 'boolean') return invalid();
  if (detail !== undefined && detail !== null && typeof detail !== 'string') return invalid();
  const text = answer && question.detailLabel ? collapse(detail ?? '') : '';
  if (text.length > TEXT_MAX) return { issue: `Use até ${TEXT_MAX} caracteres no detalhe.` };
  return { value: answer, detail: text || null };
}

function readMultiple(question: RecordQuestion, value: unknown): Read {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) return invalid();
  const picked = new Set(value as string[]);
  if (!picked.size) return undefined;
  if ([...picked].some((id) => !question.options.some((option) => option.id === id))) {
    return { issue: 'Escolha entre as opções da lista.' };
  }
  const exclusive = question.options.find((option) => option.id === question.exclusiveOptionId);
  if (exclusive && picked.has(exclusive.id) && picked.size > 1) {
    return { issue: `"${exclusive.label}" não combina com as outras opções.` };
  }
  // A ordem gravada é a do modelo, não a dos cliques: duas versões com as mesmas marcações não
  // podem aparecer como mudança só porque a pessoa clicou em outra ordem.
  return question.options.filter((option) => picked.has(option.id)).map((option) => option.id);
}

function readNumber(value: unknown): Read {
  const text = typeof value === 'number' ? String(value) : value;
  if (typeof text !== 'string') return invalid();
  const normalized = text.trim().replace(',', '.');
  if (!normalized) return undefined;
  return /^-?\d{1,9}(\.\d{1,4})?$/u.test(normalized)
    ? normalized
    : { issue: 'Informe um número com até quatro casas decimais.' };
}

function readDate(value: unknown): Read {
  if (typeof value !== 'string' || !isDate(value)) return { issue: 'Informe uma data válida.' };
  return value;
}

function isDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && year >= 1900;
}

/// A resposta como uma pessoa lê, numa linha. Nulo quando não há resposta.
export function answerText(
  question: RecordQuestion,
  answer: RecordAnswer | undefined,
): string | null {
  if (answer === undefined) return null;
  switch (question.type) {
    case 'YES_NO': {
      const { value, detail } = answer as YesNoAnswer;
      return value ? `Sim${detail ? ` — ${detail}` : ''}` : 'Não';
    }
    case 'SINGLE_CHOICE':
      return optionLabels(question, [answer as string]);
    case 'MULTIPLE_CHOICE':
      return optionLabels(question, answer as string[]);
    case 'DECLARATION':
      return 'Marcada';
    case 'LONG_TEXT':
      return richTextToPlain(answer as string);
    case 'NUMBER':
      return (answer as string).replace('.', ',');
    case 'DATE':
      return (answer as string).split('-').reverse().join('/');
    case 'SHORT_TEXT':
      return answer as string;
  }
}

function optionLabels(question: RecordQuestion, ids: readonly string[]): string {
  return ids
    .map((id) => question.options.find((option) => option.id === id)?.label ?? 'opção removida')
    .join(', ');
}

// ---------------------------------------------------------------------------------------------
// Quadro de saúde
// ---------------------------------------------------------------------------------------------

/// O que a resposta diz sobre o tema da pergunta: uma declaração (com o que foi declarado), uma
/// negação ou nada. Só o sim ou não nega: "Nenhuma" numa lista não contradiz a alergia que outra
/// ficha declarou, porque as listas variam de ficha para ficha.
export function healthSignal(
  question: RecordQuestion,
  answer: RecordAnswer | undefined,
): { kind: 'DECLARED'; value: string } | { kind: 'DENIED' } | null {
  if (!question.themeKey || answer === undefined) return null;
  if (question.type === 'YES_NO') {
    const { value, detail } = answer as YesNoAnswer;
    return value ? { kind: 'DECLARED', value: detail ?? 'Sim' } : { kind: 'DENIED' };
  }
  const picked = question.type === 'SINGLE_CHOICE' ? [answer as string] : (answer as string[]);
  const counted = picked.filter((id) =>
    question.type === 'SINGLE_CHOICE'
      ? question.flaggedOptionIds.includes(id)
      : id !== question.exclusiveOptionId,
  );
  return counted.length ? { kind: 'DECLARED', value: optionLabels(question, counted) } : null;
}

export interface HealthRecordSource {
  templateId: string;
  templateName: string;
  version: number;
  recordedAt: string;
  blocks: readonly RecordBlock[];
  answers: RecordAnswers;
}

export interface HealthDeclaration {
  themeKey: string;
  value: string;
  questionId: string;
  questionLabel: string;
  templateId: string;
  templateName: string;
  version: number;
  recordedAt: string;
}

export type HealthDenial = Omit<HealthDeclaration, 'value'>;

/// Um aviso por tema: o que declarou e o que negou, cada um com a pergunta. Um aviso por par de
/// perguntas repetiria o mesmo cartão para quem nega em duas perguntas do tema ("Gestante?" e
/// "Lactante?") — e sem a pergunta ninguém entenderia por que o Não aparece duas vezes.
export interface HealthDivergence {
  themeKey: string;
  declared: HealthDeclaration[];
  denied: HealthDenial[];
}

/// A parte do Quadro que vem das fichas: a união das respostas positivas das versões vigentes e o
/// aviso de divergências. Basta uma ficha declarar para aparecer (ADR 0038, decisão 8).
export function summarizeHealth(
  records: readonly HealthRecordSource[],
  themes: readonly Pick<HealthTheme, 'key' | 'flagsDivergence'>[],
): { declarations: HealthDeclaration[]; divergences: HealthDivergence[] } {
  const declarations: HealthDeclaration[] = [];
  const denials: HealthDenial[] = [];
  for (const record of records) {
    for (const question of questionsOf(record.blocks)) {
      const signal = healthSignal(question, record.answers[question.id]);
      if (!signal) continue;
      const source = {
        themeKey: question.themeKey!,
        questionId: question.id,
        questionLabel: question.label,
        templateId: record.templateId,
        templateName: record.templateName,
        version: record.version,
        recordedAt: record.recordedAt,
      };
      if (signal.kind === 'DECLARED') declarations.push({ ...source, value: signal.value });
      else denials.push(source);
    }
  }
  const divergences = themes
    .filter((theme) => theme.flagsDivergence)
    .flatMap((theme) => {
      const declared = declarations.filter((item) => item.themeKey === theme.key);
      // O Não só contradiz o Sim de outra ficha: dentro da mesma ficha, as perguntas do tema são
      // fatos diferentes que a própria cliente respondeu juntas.
      const denied = denials.filter(
        (item) =>
          item.themeKey === theme.key &&
          declared.some((declaration) => declaration.templateId !== item.templateId),
      );
      return denied.length ? [{ themeKey: theme.key, declared, denied }] : [];
    });
  return { declarations, divergences };
}

// ---------------------------------------------------------------------------------------------
// Comparação entre versões
// ---------------------------------------------------------------------------------------------

export interface RecordVersionContent {
  blocks: readonly RecordBlock[];
  answers: RecordAnswers;
}

export interface RecordAnswerChange {
  questionId: string;
  label: string;
  before: string | null;
  after: string | null;
  /// A pergunta existia numa das versões do modelo e não existe na outra.
  outsideTemplate: 'BEFORE' | 'AFTER' | null;
}

/// Antes e depois por pergunta. Cada lado é lido com as perguntas da versão do modelo que ele
/// respondeu — nada reinterpreta o passado (ADR 0038, decisão 4).
export function compareRecordVersions(
  before: RecordVersionContent | null,
  after: RecordVersionContent,
): RecordAnswerChange[] {
  const beforeQuestions = before ? questionsOf(before.blocks) : [];
  const afterQuestions = questionsOf(after.blocks);
  const ordered = [
    ...afterQuestions,
    ...beforeQuestions.filter(
      (question) => !afterQuestions.some((item) => item.id === question.id),
    ),
  ];
  return ordered.flatMap((question) => {
    const old = beforeQuestions.find((item) => item.id === question.id);
    const current = afterQuestions.find((item) => item.id === question.id);
    const beforeText = old ? answerText(old, before!.answers[old.id]) : null;
    const afterText = current ? answerText(current, after.answers[current.id]) : null;
    if (beforeText === afterText) return [];
    return [
      {
        questionId: question.id,
        label: question.label,
        before: beforeText,
        after: afterText,
        outsideTemplate: before && !old ? 'BEFORE' : !current ? 'AFTER' : null,
      },
    ];
  });
}

/// As respostas que seguem para o modelo novo: só as das perguntas que continuam, e só as que
/// ainda cabem nelas — a opção que saiu da lista não volta marcada.
export function carryAnswers(
  blocks: readonly RecordBlock[],
  answers: RecordAnswers,
): RecordAnswers {
  const carried: RecordAnswers = {};
  for (const question of questionsOf(blocks)) {
    const read = readAnswer(question, answers[question.id]);
    if (read !== undefined && !isIssue(read)) carried[question.id] = read;
  }
  return carried;
}

function collapse(value: string): string {
  return value.replace(/\s+/gu, ' ').trim();
}

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

// ---------------------------------------------------------------------------------------------
// Sessão
// ---------------------------------------------------------------------------------------------

/// O tipo do modelo, escolhido ao criar e imutável: a ficha da cliente, versionada e assinável, ou a
/// checagem respondida a cada atendimento (ADR 0038, decisão 2 e adendo de 2026-09-28).
export const RECORD_TEMPLATE_KINDS = ['RECORD', 'CHECK'] as const;
export type RecordTemplateKind = (typeof RECORD_TEMPLATE_KINDS)[number];

/// Os tipos de intercorrência, em lista fechada (ADR 0038, decisão 10).
export const OCCURRENCE_TYPES = [
  'ALLERGIC_REACTION',
  'IRRITATION',
  'CHEMICAL_BURN',
  'HAIR_BREAKAGE',
  'OTHER',
] as const;
export type OccurrenceType = (typeof OCCURRENCE_TYPES)[number];

export const OCCURRENCE_TYPE_LABELS: Record<OccurrenceType, string> = {
  ALLERGIC_REACTION: 'Reação alérgica',
  IRRITATION: 'Irritação',
  CHEMICAL_BURN: 'Queimadura química',
  HAIR_BREAKAGE: 'Quebra do fio',
  OTHER: 'Outro',
};

export const OCCURRENCE_SEVERITIES = ['MILD', 'MODERATE', 'SEVERE'] as const;
export type OccurrenceSeverity = (typeof OCCURRENCE_SEVERITIES)[number];

export const OCCURRENCE_SEVERITY_LABELS: Record<OccurrenceSeverity, string> = {
  MILD: 'Leve',
  MODERATE: 'Moderada',
  SEVERE: 'Grave',
};

// ---------------------------------------------------------------------------------------------
// Assinatura
// ---------------------------------------------------------------------------------------------

/// O que o modelo pede: "Opcional" (padrão) ou "Pedir em toda versão", que marca a versão sem
/// assinatura como pendente, sem travar nada (ADR 0038, decisão 13).
export const RECORD_SIGNATURE_POLICIES = ['OPTIONAL', 'EVERY_VERSION'] as const;
export type RecordSignaturePolicy = (typeof RECORD_SIGNATURE_POLICIES)[number];

/// Traçada na tela ou via assinada anexada.
export const RECORD_SIGNATURE_KINDS = ['DIGITAL', 'PHYSICAL'] as const;
export type RecordSignatureKind = (typeof RECORD_SIGNATURE_KINDS)[number];

/// Um traço é a sequência de pontos `[x, y]` de quando o dedo, a caneta ou o mouse encostou até
/// soltar. As duas coordenadas são relativas à LARGURA da área: `x` vai de 0 a 1 e `y` passa de 1
/// quando a área é mais alta que larga. Assim a assinatura se redesenha igual em qualquer tamanho,
/// na tela e no PDF, sem distorcer.
export type SignatureStroke = Array<[number, number]>;

export const SIGNATURE_LIMITS = Object.freeze({
  strokes: 200,
  pointsPerStroke: 3000,
  points: 30_000,
  /// A área mais alta que se aceita, em larguras: a tela cheia de um celular em pé.
  maxY: 4,
});

/// Confere os traços que chegam e os devolve arredondados a quatro casas — precisão de um décimo de
/// milésimo da largura, muito além do que um dedo desenha. Rabisco de um ponto só não é assinatura.
export function reviewSignatureStrokes(
  value: unknown,
): { strokes: SignatureStroke[] } | { issue: string } {
  const empty = { issue: 'Assine na área tracejada antes de confirmar.' };
  if (!Array.isArray(value) || value.length === 0) return empty;
  if (value.length > SIGNATURE_LIMITS.strokes) return { issue: 'Assinatura com traços demais.' };
  const strokes: SignatureStroke[] = [];
  let total = 0;
  for (const stroke of value as unknown[]) {
    if (!Array.isArray(stroke) || stroke.length === 0) return { issue: 'Traço inválido.' };
    if (stroke.length > SIGNATURE_LIMITS.pointsPerStroke) {
      return { issue: 'Assinatura com pontos demais.' };
    }
    const points: SignatureStroke = [];
    for (const point of stroke as unknown[]) {
      if (!isPoint(point)) return { issue: 'Traço inválido.' };
      points.push([round(point[0]), round(point[1])]);
    }
    total += points.length;
    strokes.push(points);
  }
  if (total > SIGNATURE_LIMITS.points) return { issue: 'Assinatura com pontos demais.' };
  return strokes.some((stroke) => stroke.length >= 3) && total >= 10 ? { strokes } : empty;
}

function isPoint(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate)) &&
    value[0] >= 0 &&
    value[0] <= 1 &&
    value[1] >= 0 &&
    value[1] <= SIGNATURE_LIMITS.maxY
  );
}

function round(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/// O retângulo que os traços ocupam, com uma folga pequena: é o que a tela e o PDF enquadram, para
/// a assinatura não aparecer perdida num canto da área em que foi feita.
export function signatureBounds(strokes: readonly SignatureStroke[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const points = strokes.flat();
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const pad = 0.02;
  const x = Math.max(0, Math.min(...xs) - pad);
  const y = Math.max(0, Math.min(...ys) - pad);
  return {
    x,
    y,
    width: Math.max(0.05, Math.max(...xs) + pad - x),
    height: Math.max(0.05, Math.max(...ys) + pad - y),
  };
}
